import { unixEpoch } from '@/utils/date-util'

/**
 * Workspace Features.
 */
export interface WorkspaceConfig {
  billing: BillingConfig
  termsOfService: TermsOfServiceConfig
  signup: SignupConfig
  jobs: JobConfig
  jobProgression: JobProgressionConfig
  notifications: NotificationsConfig
  messageBank: MessageBankConfig
  combinedPricing: CombinedPricingConfig
  payouts: PayoutsConfig
  sureprep: SureprepConfig
  insights: InsightsConfig
  contract: ContractConfig
  staffing: StaffingConfig
  legendsBuilder: LegendsBuilderConfig
  externalCustomer: ExternalCustomerConfig
  callCenter: CallCenterConfig
  customMenu: CustomMenuItem[]
}

/**
 * Configuration for Billing.
 */
export interface BillingConfig {
  enabled: boolean
  couponsEnabled: boolean
  stripeConnectId?: string
}

/**
 * Configuration for Terms of Service.
 */
export interface TermsOfServiceConfig {
  showOnLogin: boolean
  hideOnWortal: boolean
  customTermsEnabled: boolean
}

/**
 * Configuration for SignUp.
 */
export interface SignupConfig {
  logoutUrl?: string
  federatedLogout: boolean
  externalProfiles: boolean
}

/**
 * Configuration for Jobs.
 */
export interface JobConfig {
  anonymousOpportunities: boolean
  progressionFlow: boolean
}

/**
 * Progression flow a legend uses.
 */
export type ProgressionFlow =
  'OutsourcingProgression' | 'RetailProgression' | 'None' | 'Unknown'

/**
 * Configuration for Job Progression.
 */
export interface JobProgressionConfig {
  enabled: boolean
  /**
   * Progression flow by legend ID.
   */
  legends: Record<string, ProgressionFlow>
}

/**
 * Configuration for Notifications.
 */
export interface NotificationsConfig {
  allowSnoozing: boolean
}

export interface MessageBankConfig {
  enabled: boolean
}

export interface CombinedPricingConfig {
  jobScopeChangeRequestsEnabled: boolean
  jobScopeRequireClientApproval: boolean
}

export interface PayoutsConfig {
  enabled: boolean
}

export interface SureprepConfig {
  enabled: boolean
}

/**
 * Configuration for Insights shown within jobs.
 */
export interface InsightsConfig {
  enabled: boolean
}

/**
 * Configuration for the workspace contract.
 */
export interface ContractConfig {
  enabled: boolean
  amount: number
  vmFees: VirtualMachineFeeConfig
  setupFeeAmount: number
  startDate: Date
  endDate: Date
}

export interface VirtualMachineFeeConfig {
  amountPerUnit: number
  quantity: number
}

export interface StaffingConfig {
  enabled: boolean
}

/**
 * Configuration for the legend builder.
 */
export interface LegendsBuilderConfig {
  enabled: boolean
}

/**
 * Configuration for External Customer.
 */
export interface ExternalCustomerConfig {
  enabled: boolean
  allowRequestingMagicLinks: boolean
  /**
   * Whether advisors can be assigned to customers. Advisors inviting a
   * customer are assigned to them automatically.
   */
  clientAssignmentEnabled: boolean
}

export interface CallCenterConfig {
  enabled: boolean
  totalRequiredQuestions: number
}

/**
 * Application a custom menu item targets.
 */
export type CustomMenuItemTarget = 'Portal' | 'HQ' | 'Mobile' | 'All'

/**
 * A custom link in the menu.
 */
export interface CustomMenuItem {
  label: string
  target: CustomMenuItemTarget
  /**
   * ID of the role the item is shown to.
   */
  audience: string
  url: string
}

export const WorkspaceConfig = {
  default: {
    billing: {
      enabled: false,
      couponsEnabled: false,
    },
    termsOfService: {
      showOnLogin: false,
      hideOnWortal: false,
      customTermsEnabled: false,
    },
    signup: {
      federatedLogout: false,
      externalProfiles: false,
    },
    jobs: {
      anonymousOpportunities: false,
      progressionFlow: false,
    },
    jobProgression: {
      enabled: false,
      legends: {},
    },
    notifications: {
      allowSnoozing: false,
    },
    messageBank: {
      enabled: false,
    },
    combinedPricing: {
      jobScopeChangeRequestsEnabled: false,
      jobScopeRequireClientApproval: false,
    },
    payouts: {
      enabled: false,
    },
    sureprep: {
      enabled: false,
    },
    insights: {
      enabled: false,
    },
    contract: {
      enabled: false,
      amount: 0,
      vmFees: { amountPerUnit: 0, quantity: 0 },
      setupFeeAmount: 0,
      startDate: unixEpoch(),
      endDate: unixEpoch(),
    },
    staffing: {
      enabled: false,
    },
    legendsBuilder: {
      enabled: false,
    },
    externalCustomer: {
      enabled: false,
      allowRequestingMagicLinks: false,
      clientAssignmentEnabled: false,
    },
    callCenter: {
      enabled: false,
      totalRequiredQuestions: 0,
    },
    customMenu: [],
  } as WorkspaceConfig,
}
